import * as React from "react"

import { cn } from "../../cn"
import { Label } from "../Label/Label"

export interface FormFieldProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Rendered as the control's label. Omit for a control that labels itself. */
  label?: React.ReactNode
  /** Guidance shown under the label, before the control. */
  description?: React.ReactNode
  /** Replaces the description while set, and marks the control invalid. */
  error?: React.ReactNode
  /** Appends "(optional)" to the label. */
  optional?: boolean
  tooltip?: React.ReactNode
  /** Passed to the label; see `LabelProps.tooltipIcon`. */
  tooltipIcon?: "help" | "info"
  /**
   * The control. Given a render function, it receives the ids to wire onto the
   * control so the label, description, and error are announced with it.
   */
  children:
    | React.ReactNode
    | ((ids: {
        id: string
        "aria-labelledby": string
        "aria-describedby": string | undefined
        "aria-invalid": boolean | undefined
      }) => React.ReactNode)
}

/**
 * A single control with its label, guidance, and error, spaced consistently.
 * Every settings form uses this so fields line up across pages.
 */
const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      className,
      label,
      description,
      error,
      optional,
      tooltip,
      tooltipIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId()
    const controlId = `${generatedId}-control`
    const labelId = `${generatedId}-label`
    const messageId = `${generatedId}-message`
    const hasMessage = Boolean(error ?? description)

    const control =
      typeof children === "function"
        ? children({
            id: controlId,
            "aria-labelledby": labelId,
            "aria-describedby": hasMessage ? messageId : undefined,
            "aria-invalid": error ? true : undefined,
          })
        : children

    return (
      <div ref={ref} className={cn("space-y-1.5", className)} {...props}>
        {label && (
          <Label
            id={labelId}
            htmlFor={controlId}
            tooltip={tooltip}
            tooltipIcon={tooltipIcon}
          >
            {label}
            {optional && (
              <span className="font-normal text-coval-text-muted">
                (optional)
              </span>
            )}
          </Label>
        )}
        {description && !error && (
          <p id={messageId} className="text-xs text-coval-text-muted">
            {description}
          </p>
        )}
        {control}
        {error && (
          <p id={messageId} className="text-xs text-coval-danger-text">
            {error}
          </p>
        )}
      </div>
    )
  },
)

FormField.displayName = "FormField"

export type FormFieldsetProps = React.HTMLAttributes<HTMLDivElement>

/** Stacks `FormField`s with consistent spacing between them. */
const FormFieldset = React.forwardRef<HTMLDivElement, FormFieldsetProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("space-y-4", className)} {...props} />
  ),
)

FormFieldset.displayName = "FormFieldset"

export type FormActionsProps = React.HTMLAttributes<HTMLDivElement>

/** Trailing action row for a form, separated from the fields above it. */
const FormActions = React.forwardRef<HTMLDivElement, FormActionsProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center justify-end gap-2 pt-2", className)}
      {...props}
    />
  ),
)

FormActions.displayName = "FormActions"

export { FormActions, FormField, FormFieldset }
